// SidebarBucketContent.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Hooks
import useSidebarSelection from '../hooks/useSidebarSelection';

// Constants
import { SIDEBAR_CHANNELS_EMPTY_HINT } from './constants/sidebarBucketEmptyHints';

// CSS
import './SidebarBucketContent.css';

// Icons — restore with Dashboards section
// import Icon from 'svg/Icon';

// Components
import SidebarBucketAddButton from './addbutton/SidebarBucketAddButton';
import LibrarySidebarSection from './library/LibrarySidebarSection';
// Watches temporarily hidden in the sidebar.
// import WatchesSidebar from 'watch/WatchesSidebar';
import SidebarButton from 'main/sidebar/button/SidebarButton';

// Hooks / entitlements
import useBaseEntitlements from 'hooks/useBaseEntitlements';
import {
    canUseApps,
    canUseChannels,
    // canUseDashboards, // restore with Dashboards section
    canUseDms,
} from 'utilities/planSurfaceEntitlements';
import { NODE_MODULE } from 'utilities/nodeModule';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';

/**
 * SidebarBucketContent
 *
 * Renders the conversation, channel, database, and dashboard sections for a sidebar bucket.
 *
 * @param {object} bucket - Bucket data with dashboards, databases, and channels.
 * @param {boolean} isOpen - Whether the bucket content should be expanded.
 * @param {boolean} showHeader - Whether the parent bucket renders a header.
 * @param {function} onDashboardClick - Called when a dashboard row is clicked.
 * @param {function} onChannelClick - Called when a channel row is clicked.
 * @param {function} onDatabaseClick - Called when a database row is clicked.
 * @param {function} onAddFromTemplate - Called when the add dashboard action is clicked.
 * @param {function} onAddChannel - Called when the add channel action is clicked.
 * @param {function} onAddDatabase - Called when the add database action is clicked.
 *
 * @returns {ReactNode} The rendered sidebar bucket content.
 */
const SidebarBucketContent = ({
    bucket,
    isOpen,
    showHeader,
    onDashboardClick,
    onChannelClick,
    onDatabaseClick,
    onAddFromTemplate,
    onAddChannel,
    onAddDatabase,
    onConversationClick,
    onCreateMessage,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const { isSidebarRailCompact } = useUI();
    const { user, userSettings } = useUser();
    const { activeBase } = useBase();
    const compact = Boolean(isSidebarRailCompact);

    const activeBaseKey = String(
        activeBase?.key || activeBase?.id || getActiveBaseKeyFromSettings(userSettings) || ''
    ).trim();

    // ----- HOOKS ----- //

    const {
        isDatabaseSelected,
        // isDashboardSelected, // restore with Dashboards section
        isChannelSelected,
    } = useSidebarSelection({
        user,
        activeBaseKey,
    });

    const { entitlements } = useBaseEntitlements({
        isActive: Boolean(activeBaseKey),
        baseKey: activeBaseKey,
    });
    const features = entitlements?.features || null;
    const showChannels = canUseChannels(features);
    const showDms = canUseDms(features);
    // const showDashboards = canUseDashboards(features); // restore with Dashboards section
    const showApps = canUseApps(features);

    const collectionDatabases = (Array.isArray(bucket?.databases) ? bucket.databases : [])
        .filter((row) => {
            const typeKey = String(row?.database?.type || row?.type || '').trim().toUpperCase();
            return Boolean(typeKey);
        });

    // ----- RENDER ----- //

    return (
        <div
            className={[
                'sidebar-bucket-content',
                isOpen ? 'is-open' : '',
                compact ? 'is-rail-compact' : '',
            ].filter(Boolean).join(' ')}
            style={{
                padding: compact ? 0 : (showHeader ? '10px 10px 10px 20px' : 0),
            }}
        >

            {bucket?.key === 'base' ? (
                <>
                    {/* <WatchesSidebar /> */}

                    <LibrarySidebarSection
                        afterMenu={showChannels ? (
                            <>
                                {!compact ? (
                                    <div className="sidebar-bucket-heading">
                                        <div className="sidebar-bucket-subtitle library-sidebar-section-title-row">
                                            <span>Channels</span>
                                        </div>
                                    </div>
                                ) : null}

                                {bucket?.channels?.length > 0 ? (
                                    <div className="sidebar-bucket-list">
                                        {bucket.channels.map((channelRow) => (
                                            <SidebarButton
                                                key={channelRow.key}
                                                iconKey={channelRow?.iconKey || 'tag'}
                                                label={channelRow?.title || 'Untitled'}
                                                selected={isChannelSelected(channelRow.key)}
                                                onClick={() => onChannelClick?.(channelRow)}
                                            />
                                        ))}
                                    </div>
                                ) : (!compact ? (
                                    <p className="sidebar-bucket-empty-hint">
                                        {SIDEBAR_CHANNELS_EMPTY_HINT}
                                    </p>
                                ) : null)}

                                {!compact ? (
                                    <SidebarBucketAddButton
                                        label="New Channel"
                                        onClick={() => onAddChannel?.(bucket)}
                                    />
                                ) : null}
                            </>
                        ) : null}
                        databases={collectionDatabases}
                        isDatabaseSelected={isDatabaseSelected}
                        onAddDatabase={onAddDatabase}
                        onDatabaseClick={onDatabaseClick}
                    />

                    {showDms && bucket?.conversations?.length > 0 && (
                        <>
                            {!compact ? (
                                <div className="sidebar-bucket-subtitle">
                                    Direct Messages
                                </div>
                            ) : null}

                            <div className="sidebar-bucket-list">
                                {bucket.conversations.map((conversationRow) => (
                                    <SidebarButton
                                        key={conversationRow.key}
                                        iconKey={conversationRow?.iconKey || 'chat'}
                                        label={conversationRow?.title || 'Conversation'}
                                        selected={isChannelSelected(conversationRow.key)}
                                        rightContent={conversationRow?.unread ? (
                                            <span
                                                style={{
                                                    backgroundColor: theme.foreground,
                                                    borderRadius: '999px',
                                                    display: 'inline-block',
                                                    flexShrink: 0,
                                                    height: 8,
                                                    width: 8,
                                                }}
                                                aria-hidden
                                            />
                                        ) : null}
                                        onClick={() => onConversationClick?.(conversationRow)}
                                    />
                                ))}
                            </div>

                            {!compact ? (
                                <SidebarBucketAddButton
                                    label="New Message"
                                    onClick={() => onCreateMessage?.()}
                                />
                            ) : null}
                        </>
                    )}
                </>
            ) : null}

            {/*
            {showApps && bucket?.installedApps?.length > 0 ? (
                <>
                    {!compact ? (
                        <div className="sidebar-bucket-subtitle">
                            Apps
                        </div>
                    ) : null}

                    <div className="sidebar-apps-groups">
                        {bucket.installedApps.map((appGroup) => (
                            <div className="sidebar-apps-group" key={appGroup.key}>
                                {!compact ? (
                                    <div className="sidebar-apps-group-title">
                                        {appGroup.title}
                                    </div>
                                ) : null}

                                <div className="sidebar-bucket-list sidebar-apps-group-list">
                                    {appGroup.databases.map((row) => (
                                        <SidebarButton
                                            key={row.key}
                                            iconKey={row.iconKey}
                                            label={row.label}
                                            selected={isDatabaseSelected(row.key)}
                                            onClick={() => onDatabaseClick?.(
                                                row.database,
                                                NODE_MODULE.PRIVATE,
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
            */}

            {/* Dashboards temporarily hidden from the sidebar menu — restore later.
            {showDashboards ? (
                <>
                    {!compact ? (
                        <div className="sidebar-bucket-subtitle">
                            Dashboards
                        </div>
                    ) : null}

                    {bucket?.dashboards?.length > 0 && (
                        <div className="sidebar-bucket-list">
                            {bucket.dashboards.map((dashboardRow) => {
                                const showPinned = dashboardRow?.pinned === true;
                                const showPrivate = dashboardRow?.private === true;

                                return (
                                    <SidebarButton
                                        key={dashboardRow.key}
                                        iconKey={dashboardRow?.iconKey || 'dashboard'}
                                        label={dashboardRow?.title || 'Untitled Dashboard'}
                                        selected={isDashboardSelected(dashboardRow.key)}
                                        rightContent={showPinned || showPrivate ? (
                                            <div
                                                style={{
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    gap: 6,
                                                }}
                                            >
                                                {showPinned && (
                                                    <Icon
                                                        iconKey="keep"
                                                        color={theme.foregroundMuted}
                                                        width={16}
                                                        height={16}
                                                    />
                                                )}
                                                {showPrivate && (
                                                    <Icon
                                                        iconKey="lock"
                                                        color={theme.foregroundMuted}
                                                        width={16}
                                                        height={16}
                                                    />
                                                )}
                                            </div>
                                        ) : null}
                                        onClick={() => onDashboardClick?.(dashboardRow)}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {bucket?.key === 'base' && !compact ? (
                        <SidebarBucketAddButton
                            label="New Dashboard"
                            onClick={() => onAddFromTemplate?.()}
                        />
                    ) : null}
                </>
            ) : null}
            */}
        </div>
    );
};

// Dev Tools
SidebarBucketContent.displayName = 'SidebarBucketContent';

export default SidebarBucketContent;
